import React, { useEffect, useState } from 'react';
import styles from './profile-view.module.scss';
import { Avatar, BottomSheet, Button, Container, Spiner, Tag, Typography } from '@/ui-kit';
import { useParams } from 'react-router-dom';
import { useGetProfileById } from '@/hooks/use-get-profile-by-id';
import { GalleryImages } from '@/components/gallery-images';
import { differenceInYearsResult as getAge } from '@/utils/difference-in-years-result';
import clsx from 'clsx';
import { PremiumIcon, ArrowTopFillIcon, BannedUserIcon, LocationIcon, VerifiedAccountIcon, MagicIcon } from '@/assets/icons';
import { OnlineStatus, OfflineStatus } from '@/assets/icons';
import { dataPurposes } from '@/constans/purposes';
import { useDrag } from '@use-gesture/react';
import { Profile2ChatBtn } from '../profile-2-chat-btn/profile-2-chat-btn';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useNavHistory } from '@/store/useNavHistory';
import { eventsApi } from '@/api';
import { ComplainBtn } from '@/components/complain-btn/complain-btn';
import { HeaderMenu } from '@/components/header-menu';
import { bannedSubtitle } from '@/utils/banned';
import { ProfileViewModel } from '@/types/vm/profile';
import { ProfileTransformer } from '@/types/transformers/profile';
import { BadgePremium } from '@/components/badge-premium';
import badgePriority from '@/assets/images/subscription/badge-priority.svg';
import { routesApp } from '@/constans/routes';
import { useAuth } from '@/store/useAuth';
import { PremiumBottomsheet } from '@/components/premium-bottomsheet';
import { getProfileNameAndAge, travelPlansLabel } from '@/utils/msgs';

interface Props {
    id?: number;
}

export const ProfileView: React.FC<Props> = (props) => {
    const id = props.id?.toString() ?? useParams<{ id: string }>().id;
    const profileId = Number.isNaN(Number(id)) ? '' : Number(id);
    const { data: profileData, isError, isLoading } = useGetProfileById(profileId);
    const [isVisibleDetails, setIsVisibleDetails] = useState(false);

    const currentPurpose = dataPurposes.find((purpose) => purpose.value === profileData?.data?.data?.profile?.purpose);

    const getIconComponent = (icon: React.FC<React.SVGProps<SVGSVGElement>>): React.ReactNode => {
        const IconComponent = icon;

        return <IconComponent />;
    };

    const isPageAtTop = (): boolean => {
        return document.body.scrollTop === 0;
    };

    const bindSwipe = useDrag(
        ({ down, movement: [_, my] }) => {
            if (!down && my > 5 && isVisibleDetails && isPageAtTop()) {
                setIsVisibleDetails(false);
            }
        },
        { axis: 'y' },
    );

    const toggleVisibleDetails = () => {
        setIsVisibleDetails((prev) => {
            const nextState = !prev;

            if (!nextState) {
                const topBlock = document.querySelector(`.${styles.card}`);
                if (topBlock) {
                    topBlock.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }
            }

            return nextState;
        });
    };

    const { prevRoute } = useNavHistory(state => state);

    const [isVerificationModalOpen, setIsVerificationModalOpen] =
        useState(false);

    const userData = useAuth((state) => state.getUser());
    
    const [isPremiumModalOpen, setIsPremiumModalOpen] =
        useState(false);

    const [profileViewModel, setProfileViewModel] = useState(
        new ProfileViewModel()
    )

    useEffect(() => {
        if (!profileData) return
        setProfileViewModel(
            new ProfileViewModel()
                .setData(
                    ProfileTransformer
                        .convertNetworkResponseFromGetProfileById(
                            profileData
                        )
                )
        )
    }, [profileData])

    const [photoIndex, setPhotoIndex] = useState<number | null>(null)

    const calcShowRestrictGalleryWidget = (index: number) => {
        return ((userData?.photos?.length ?? 0) - 1 < index) &&
            !(userData?.is_paid ?? false) &&
            userData?.state !== 'half_done' &&
            prevRoute !== routesApp.messages &&
            !prevRoute?.startsWith('/messages/chat/') &&
            (userData?.photo_parity_enabled ?? false)
    }
    
    const showRestrictGalleryWidget = calcShowRestrictGalleryWidget(photoIndex ?? 0)

    return isLoading ? (
        <Spiner />
    ) : (profileViewModel.profile?.is_banned ?? false) ? (
            <div className={styles.bannedProfile}>
                <HeaderMenu
                    title="Аккаунт"
                    marginBottom={20} />
                
                <Container className={clsx(styles.container, styles.bannedProfileContent)}>
                    <div className={styles.bannedAccountProfileWrapper}>
                        <div className={styles.bannerAvatarWrapper}>
                            <Avatar
                                src={profileViewModel.bannedAvatar}
                                size={120} />
                        </div>
                        <Typography className={styles.bannerAvatarProfileName} center>
                            {
                                [                                    
                                    profileViewModel.name,
                                    ...(
                                        profileViewModel.age ?
                                            [profileViewModel.age]
                                        :
                                            []
                                    )
                                ].join(', ')
                            }
                        </Typography>

                        <Button
                            className={styles.bannedAccountProfileBtn}
                            textColor="black"
                            disabled>
                            <span>Аккаунт заблокирован</span>
                        </Button>

                        <div className={styles.bannedBlock}>
                            <BannedUserIcon
                                className={styles.bannedBlockIcon}
                                width={32}
                                height={32} />
                            <div className={styles.bannedBlockInfo}>
                                <Typography
                                    variant="h6"
                                    fontFamily="SFProText"
                                    fontWeight="semibold"
                                    center
                                    className={styles.bannedBlockInfoTitle}>
                                    {profileViewModel.accountBannedMsg}
                                </Typography>
                                <Typography
                                    variant="p"
                                    fontFamily="SFProText"
                                    center
                                    className={styles.bannedBlockInfoSubtitle}>
                                    {bannedSubtitle}
                                </Typography>
                            </div>
                        </div>

                    </div>
                </Container>
            </div>
    ) : (
        <>
            <div className={clsx(styles.container, isVisibleDetails && styles.visibleDetails)}>
                <div className={styles.card}>
                    {
                        <GalleryImages
                            photos={profileData?.data?.data?.profile.photos ?? []}
                            onSlideChange={(index) => {
                                if (userData?.photo_parity_enabled) {
                                    setPhotoIndex(index)
                                }
                            }} />
                    }
                    {
                        !showRestrictGalleryWidget &&
                        <div className={clsx(styles.cardInfo, isVisibleDetails && styles.visibleDetails)}>
                            <div className={styles.nameAndVerificationStatusInfo}>
                                <Typography
                                    typeColor="white"
                                    fontWeight="medium"
                                    variant="h3"
                                    className={styles.cardInfoText}>
                                    {getProfileNameAndAge(profileData?.data?.data?.profile)}
                                </Typography>
                                {
                                    profileData
                                        ?.data
                                        ?.data
                                        ?.profile
                                        ?.verification_status
                                        === 'verified' &&
                                    <VerifiedAccountIcon
                                        width={24}
                                        height={24}
                                        className={styles.statusIcon}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setIsVerificationModalOpen(true)
                                        }} />
                                }
                                {
                                    profileViewModel.isPremium() &&
                                    <div
                                        className={styles.premiumStatus}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setIsPremiumModalOpen(true)
                                        }}>
                                        <PremiumIcon width={24} height={24} />
                                        <Typography
                                            fontWeight="medium"
                                            className={styles.premiumStatusLabel}>Premium</Typography>
                                    </div>
                                }
                            </div>
                            <Typography
                                typeColor="white"
                                fontWeight="normal"
                                variant="p"
                                className={styles.descriptionIntro}>
                                {profileData?.data?.data?.profile?.about_me}
                            </Typography>
                        </div>
                    }

                    {
                        (
                            profileData
                                ?.data
                                ?.data
                                ?.chat_id &&
                            !showRestrictGalleryWidget
                        ) &&
                        <Profile2ChatBtn
                            chatId={profileData?.data?.data?.chat_id}
                            className={
                                clsx(
                                    isVisibleDetails && styles.visibleDetails,
                                    styles.openChatBtnAction
                                )
                            } />
                    }
                    
                    {
                        !showRestrictGalleryWidget &&
                        <div
                            className={clsx(styles.arrowTop, isVisibleDetails && styles.visibleDetails)}
                            onClick={(event) => toggleVisibleDetails()}>
                            <ArrowTopFillIcon />
                        </div>
                    }
                </div>

                <Container className={clsx(styles.userInfo, isVisibleDetails && styles.visibleDetails)} zIndex={10}>
                    <div
                        className={styles.userInfo__wrapper}
                        {...bindSwipe()}
                    >
                        <div className={styles.userInfo__header}>
                            <Typography
                                typeColor="black"
                                fontWeight="medium"
                                variant="span"
                                className={clsx(styles.city)}
                            >
                                {profileData?.data?.data?.profile?.is_online ? (
                                    <>
                                        <OnlineStatus /> Онлайн
                                    </>
                                ) : (
                                    <>
                                        <OfflineStatus /> Оффлайн
                                    </>
                                )}
                            </Typography>

                            {profileData?.data?.data?.profile?.regions ? (
                                profileData?.data?.data?.profile?.regions.map((region) => (
                                    <Typography
                                        typeColor="black"
                                        fontWeight="medium"
                                        variant="span"
                                        className={clsx(styles.city)}
                                    >
                                        <LocationIcon /> {region?.name}
                                    </Typography>
                                ))
                            ) : (
                                <Typography>Список локаций пуст</Typography>
                            )}
                        </div>

                        <div className={styles.nameAndVerificationStatusInfo}>
                            <Typography
                                typeColor="black"
                                fontWeight="medium"
                                variant="h3"
                                className={styles.userInfo__title}>
                                {getProfileNameAndAge(profileData?.data?.data?.profile)}
                            </Typography>
                            {
                                profileData
                                    ?.data
                                    ?.data
                                    ?.profile
                                    ?.verification_status
                                    === 'verified' &&
                                <VerifiedAccountIcon
                                    width={24}
                                    height={24}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsVerificationModalOpen(true)
                                    }} />
                            }
                            {
                                profileViewModel.isPremium() &&
                                <PremiumIcon
                                    width={24}
                                    height={24}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsPremiumModalOpen(true)
                                    }} />
                            }
                        </div>

                        {profileData?.data?.data?.profile?.about_me && (
                            <>
                                <div className={styles.label}>
                                    <Typography
                                        variant="label"
                                        fontWeight="normal"
                                        fontFamily="SFProText"
                                    >
                                        Обо мне
                                    </Typography>
                                </div>

                                <Typography
                                    className={styles.text}
                                    variant="p"
                                >
                                    {profileData?.data?.data?.profile?.about_me}
                                </Typography>
                            </>
                        )}

                        {currentPurpose && (
                            <Tag
                                icon={getIconComponent(currentPurpose.icon)}
                                className={clsx(styles.tagInfo, styles.isIcon)}
                            >
                                <div>
                                    <Typography
                                        className={styles.tagInfo__title}
                                        variant="p"
                                    >
                                        Я ищу
                                    </Typography>
                                    <Typography
                                        className={styles.tagInfo__text}
                                        variant="p"
                                        fontWeight="bold"
                                    >
                                        {currentPurpose.text}
                                    </Typography>
                                </div>
                            </Tag>
                        )}

                        {profileData?.data?.data?.profile?.travel_plans && (
                            <>
                                <div className={styles.label}>
                                    <Typography
                                        variant="label"
                                        fontWeight="normal"
                                        fontFamily="SFProText">
                                        {
                                            travelPlansLabel
                                        }
                                    </Typography>
                                </div>

                                <Typography
                                    className={styles.text}
                                    variant="p"
                                >
                                    {profileData?.data?.data?.profile?.travel_plans}
                                </Typography>
                            </>
                        )}

                        {
                            profileData
                                ?.data
                                ?.data
                                ?.chat_id &&
                            <Profile2ChatBtn chatId={profileData?.data?.data?.chat_id} />
                        }
                        
                        <ComplainBtn
                            profileId={profileId}
                            onClick={() => {
                                const event =
                                    prevRoute === '/liked' ?
                                        'report_from_like_screen'
                                    :
                                        'report_after_match'
                                callYaMetrika(event)
                                eventsApi.updateUserEvent(event)
                            }} />
                    </div>
                </Container>
            </div>
            <BottomSheet
                overlayClassName={styles.bottomsheetOverlay}
                isOpen={isVerificationModalOpen}
                isDeskorTop={false}
                onClose={() => setIsVerificationModalOpen(false)}>
                <div className={styles.verificationModalContent}>
                    <div className={styles.verificationModalContentIcon}>
                        <Avatar
                            src={profileViewModel.mainPhotoUrl}
                            size={96} />
                        <VerifiedAccountIcon
                            className={styles.verificationModalContentIconVerification}
                            width={28}
                            height={28} />
                    </div>
                    <div className={styles.verificationModalContentHeader}>
                        <Typography
                            className={styles.verificationModalContentHeaderTitle}
                            variant={'h5'}
                            fontWeight={'bold'}>
                            {
                                [
                                    profileData
                                        ?.data
                                        ?.data
                                        ?.profile
                                        .public_name,
                                    profileData
                                        ?.data
                                        ?.data
                                        ?.profile
                                        .gender === 'male' ?
                                        'прошёл'
                                    :
                                        'прошла',
                                    'проверку',
                                ].join(' ')
                            }
                        </Typography>
                        <Typography
                            className={styles.verificationModalContentHeaderDesc}>
                            Видео-верификация подтверждает, что за анкетой стоит реальный человек и что фото принадлежат ему. Это помогает сделать общение честнее и безопаснее.
                        </Typography>
                    </div>
                    <div className={styles.verificationModalContentFootnote}>
                        <Button
                            typeColor="orange"
                            className={styles.verificationModalContentFootnoteBtn}
                            onClick={() => setIsVerificationModalOpen(false)}>
                            Понятно!
                        </Button>
                    </div>
                </div>
            </BottomSheet>
            
            <PremiumBottomsheet
                photo={profileViewModel.mainPhotoUrl}
                name={profileViewModel.name}
                isPremiumModalOpen={isPremiumModalOpen}
                setIsPremiumModalOpen={setIsPremiumModalOpen} />
        </>
    );
};
